"use client";

import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { getSessionAction } from "@/actions/serverActions/auth/getSessionAction";
import LogoutIcon from "@/assets/images/logout-01.svg";
import HelpIcon from "@/assets/images/profile-help-support.svg";
import SettingsIcon from "@/assets/images/profile-settings-03.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

const menuItemClass =
  "paragraph-sm mx-1 my-0.5 gap-3 rounded-lg px-3 py-2.5 cursor-pointer";

export function ProfileMenu() {
  const router = useRouter();
  const { handleLogout } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Manager");

  useEffect(() => {
    getSessionAction()
      .then((session) => {
        if (!session?.user) return;
        const { first_name, last_name, email: userEmail } = session.user;
        setName(`${first_name} ${last_name}`.trim());
        setEmail(userEmail);
        const sessionRole = (session.user as Record<string, unknown>).role;
        if (typeof sessionRole === "string" && sessionRole)
          setRole(sessionRole);
      })
      .catch(() => {});
  }, []);

  const initials = name ? name.slice(0, 2).toUpperCase() : "?";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-full focus:outline-none"
          aria-label="Open profile menu"
        >
          <span className="flex size-9 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-600 ring-2 ring-neutral-200 transition-shadow hover:ring-primary-300">
            {initials}
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-72 rounded-xl p-0 shadow-lg"
      >
        <div className="flex items-center gap-3 p-4">
          <span className="flex size-11 items-center justify-center rounded-full bg-primary-100 text-base font-semibold text-primary-600">
            {initials}
          </span>
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex min-w-0 items-center gap-2">
              <span className="paragraph-sm truncate font-semibold text-text-primary">
                {name || "Loading…"}
              </span>
              {role && (
                <span className="label-xs shrink-0 rounded-md bg-primary-50 px-1.5 py-0.5 text-primary-600">
                  {role}
                </span>
              )}
            </div>
            <span className="paragraph-sm truncate text-text-tertiary">
              {email}
            </span>
          </div>
        </div>

        <hr className="mx-4 border-stroke-soft-200" />

        <DropdownMenuItem
          className={menuItemClass}
          onClick={() => router.push("/settings")}
        >
          <SettingsIcon className="size-5 shrink-0 text-text-secondary" />
          Settings
        </DropdownMenuItem>

        <DropdownMenuItem
          className={menuItemClass}
          onClick={() => router.push("/help")}
        >
          <HelpIcon className="size-5 shrink-0 text-text-secondary" />
          Help &amp; Support
        </DropdownMenuItem>

        <hr className="mx-4 border-stroke-soft-200" />

        <DropdownMenuItem
          className={cn(menuItemClass, "text-error-600")}
          onClick={handleLogout}
        >
          <LogoutIcon className="size-5 shrink-0" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
